// src/contexts/InspirationContext.jsx
// ===============================================
// Unified storage (Folder OR Drive) via StorageContext
// Files:
//   - inspiration/items.json   (saved images)
// Migration:
//   - reads legacy "inspiration/mudo_inspirations_v1.json" if present
// Rehydrates after "mudo:files-restored" and "mudo:vault-restored"
// ===============================================

import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useStorage } from "./StorageContext";

const FILE_ITEMS = "inspiration/items.json";
const FILE_ITEMS_LEGACY = "inspiration/mudo_inspirations_v1.json";

const InspirationContext = createContext(null);

function coerceList(v) {
  return Array.isArray(v) ? v : [];
}

export function InspirationProvider({ children }) {
  const { loadJSON, saveJSON } = useStorage();

  const [items, setItems] = useState([]);
  const loadedRef = useRef(false);
  const debounce = useRef(0);

  // ---- hydrate on boot + after restore
  useEffect(() => {
    let alive = true;

    async function hydrate() {
      try {
        // Try new location first
        let disk = await loadJSON(FILE_ITEMS, null);

        // If empty or null, attempt legacy file once
        if (!disk || !Array.isArray(disk)) {
          const legacy = await loadJSON(FILE_ITEMS_LEGACY, null);
          if (Array.isArray(legacy) && legacy.length) {
            disk = legacy;
            // Save forward into new file so future reads are fast
            try { await saveJSON(FILE_ITEMS, legacy); } catch {}
          } else {
            disk = [];
          }
        }

        if (alive) setItems(coerceList(disk));
      } catch {
        if (alive) setItems([]);
      } finally {
        loadedRef.current = true;
      }
    }

    hydrate();

    const onRestored = async () => {
      try {
        const disk = await loadJSON(FILE_ITEMS, []);
        setItems(coerceList(disk));
      } catch {}
    };
    
    const onDisconnect = () => {
      // CRITICAL: Clear all data when workspace disconnected
      if (alive) {
        setItems([]);
        loadedRef.current = false;
      }
    };
    
    window.addEventListener("mudo:vault-restored", onRestored);
    window.addEventListener("mudo:files-restored", onRestored);
    window.addEventListener("mudo:workspace-disconnected", onDisconnect);

    return () => {
      alive = false;
      window.removeEventListener("mudo:vault-restored", onRestored);
      window.removeEventListener("mudo:files-restored", onRestored);
      window.removeEventListener("mudo:workspace-disconnected", onDisconnect);
    };
  }, [loadJSON, saveJSON]);

  // ---- save when items change (debounced)
  useEffect(() => {
    if (!loadedRef.current) return;
    clearTimeout(debounce.current);
    debounce.current = setTimeout(async () => {
      try { await saveJSON(FILE_ITEMS, items); } catch {}
    }, 300);
    return () => clearTimeout(debounce.current);
  }, [items, saveJSON]);

  // ---- CRUD
  const add = (img) => {
    const id = String(img?.id || Date.now());
    const safe = {
      id,
      url: img?.url || img?.src || "",
      thumb: img?.thumb || img?.url || "",
      author: img?.author || "",
      authorUrl: img?.authorUrl || "",
      source: img?.source || "Pexels",
      sourceUrl: img?.sourceUrl || img?.pageUrl || "",
      query: img?.query || "",
      savedAt: Date.now(),
    };
    setItems((prev) =>
      prev.some((p) => String(p.id) === id) ? prev : [safe, ...prev]
    );
  };

  const remove = (id) =>
    setItems((prev) => prev.filter((p) => String(p.id) !== String(id)));

  const clear = () => setItems([]);

  const value = useMemo(() => ({ items, add, remove, clear }), [items]);

  return (
    <InspirationContext.Provider value={value}>
      {children}
    </InspirationContext.Provider>
  );
}

export function useInspiration() {
  const ctx = useContext(InspirationContext);
  if (!ctx) throw new Error("useInspiration must be used within <InspirationProvider>");
  return ctx;
}
